import { isResourceDeleting } from '../../../utils/resourceLifecycle';
import ResourceDeletionNotice from '../../common/ResourceDeletionNotice';
import React, { useState, useEffect } from 'react';
import { db } from '../../../services/firebase';
import { doc, runTransaction } from 'firebase/firestore';
import { useAttributeCategories } from '../../../contexts/AttributesContext';
import { 
  TextField, 
  Typography, 
  Box, 
  Button, 
  Dialog, 
  DialogActions, 
  DialogContent, 
  DialogTitle,
  Grid,
  Paper,
  Fade,
  alpha,
  useTheme,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { styled } from '@mui/material/styles';
import CategoryCard from './CategoryCard';
import { formatText } from '../../common/TextFormatterProvider';
import { CATEGORY_TYPE_OPTIONS, normalizeCategoryType, reservedNameError } from '../../../utils/tagValueUtils';
import { reservedKeywordError } from '../../../utils/tableCreationNames';
import DeleteResourceDialog from '../../common/DeleteResourceDialog';
import useDeleteConfirmation from '../../common/useDeleteConfirmation';

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
  borderRadius: 12,
  backgroundColor: alpha(theme.palette.background.paper, 0.8),
  backdropFilter: 'blur(8px)',
  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
  transition: 'all 0.3s ease',
  '&:hover': {
    boxShadow: '0 12px 48px rgba(0, 0, 0, 0.3)',
    transform: 'translateY(-4px)',
    borderColor: alpha(theme.palette.primary.main, 0.2),
  },
}));

const duplicateCategoryMessage = (name) => `A category named "${name}" already exists`;

function ManageAttributes({ companyName, onAddCategory }) {
  const [newCategoryDialog, setNewCategoryDialog] = useState(false);
  const [newCategory, setNewCategory] = useState({ name: '', description: '', key_tag: false, type: 'Text' });
  const [error, setError] = useState('');
  const [nameError, setNameError] = useState('');
  const categoryDeletion = useDeleteConfirmation({
    resourceType: 'tag_category',
    onPreviewError: (err) => {
      setError('Failed to check category dependencies: ' + err.message);
      console.error(err);
    },
    onDeleteError: (err) => {
      setError('Failed to delete category: ' + err.message);
      console.error(err);
    },
  });
  const theme = useTheme();

  // The category list is live (AttributesProvider), so a category added, retyped,
  // or deleted here — or by another user at the same time — shows without a
  // reload. Values are not loaded here: each CategoryCard holds its own open
  // while it is expanded.
  const { categories, loading, error: categoriesError } = useAttributeCategories({ includeDeleting: true });

  // Add event listener for opening the add category dialog
  useEffect(() => {
    const handleOpenAddCategoryDialog = () => {
      setNewCategoryDialog(true);
    };
    
    // Add event listener
    document.addEventListener('open-add-category-dialog', handleOpenAddCategoryDialog);
    
    // Cleanup
    return () => {
      document.removeEventListener('open-add-category-dialog', handleOpenAddCategoryDialog);
    };
  }, []);

  const handleAddCategory = async () => {
    try {
      if (!newCategory.name.trim()) {
        setNameError('Category name cannot be empty');
        return;
      }

      // Sanitize the category name for Firestore ID: replace spaces with underscores (keep raw for display)
      const categoryId = newCategory.name.trim().replace(/\s+/g, '_');

      if (!/^[A-Za-z][A-Za-z0-9_]*$/.test(categoryId)) {
        setNameError('Category names may contain only letters, digits, spaces, and underscores, '
          + 'and may not start with a digit or an underscore (underscores at the start are '
          + 'reserved for internal columns).');
        return;
      }

      if (categoryId === '') {
        setNameError('Category name must contain alphanumeric characters');
        return;
      }

      const reserved = reservedNameError(categoryId, 'Category name');
      if (reserved) {
        setNameError(reserved);
        return;
      }

      // A category's id becomes the name of an attribute column on every table that
      // uses it, so it may not be a word the platform keeps for its own records.
      const reservedKeyword = reservedKeywordError(categoryId, 'attribute');
      if (reservedKeyword) {
        setNameError(reservedKeyword);
        return;
      }

      const duplicate = categories.find(category => category.id === categoryId);
      if (duplicate) {
        setNameError(duplicateCategoryMessage(duplicate.name || categoryId));
        return;
      }

      // Read-then-write in one transaction: two tabs adding the same name at
      // once both derive this id, and a plain set would let the second silently
      // replace the first's document. The SDK retries on contention, so the
      // loser always reads the winner's document and is refused (M227).
      const categoryRef = doc(db, `users/${companyName}/tag_categories`, categoryId);
      await runTransaction(db, async (transaction) => {
        const existing = await transaction.get(categoryRef);
        if (existing.exists()) {
          const error = new Error(duplicateCategoryMessage(existing.data().name || categoryId));
          error.duplicate = true;
          throw error;
        }
        transaction.set(categoryRef, {
          name: newCategory.name.trim(),              // raw name for display
          name_sanitised: categoryId,                // sanitised name for logic
          type: normalizeCategoryType(newCategory.type),
          description: newCategory.description || '',
          key_tag: newCategory.key_tag,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        });
      });

      // Reset and close dialog
      setNewCategory({ name: '', description: '', key_tag: false, type: 'Text' });
      setNewCategoryDialog(false);
    } catch (err) {
      if (err.duplicate) {
        setNameError(err.message);
        return;
      }
      setError('Failed to add category: ' + err.message);
      console.error(err);
    }
  };

  const handleDeleteCategory = (categoryId) => {
    const category = categories.find(candidate => candidate.id === categoryId);
    if (!category) return;
    setError('');
    categoryDeletion.open(category);
  };

  return (
    <Box sx={{ width: '100%' }}>
      {(error || categoriesError) && (
        <Box sx={{ 
          p: 2, 
          mb: 3, 
          bgcolor: alpha(theme.palette.error.main, 0.08), 
          borderRadius: 2,
          border: `1px solid ${alpha(theme.palette.error.main, 0.15)}`,
        }}>
          <Typography color="error">{error || `Failed to load categories: ${categoriesError.message}`}</Typography>
        </Box>
      )}

      <Grid container spacing={3}>
        {categories.map((category) => (
          <Grid item xs={12} key={category.id}>
            <Fade in={true} timeout={500}>
              <Box>
                {isResourceDeleting(category) ? (
                  <ResourceDeletionNotice resource={category} onRetry={() => handleDeleteCategory(category.id)} />
                ) : (
                  <CategoryCard
                    category={{...category, name: formatText(category.name)}}
                    onDelete={handleDeleteCategory}
                    companyName={companyName}
                  />
                )}
              </Box>
            </Fade>
          </Grid>
        ))}

        {categories.length === 0 && (
          <Grid item xs={12}>
            <StyledPaper sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center',
              justifyContent: 'center',
              p: 4,
              backdropFilter: 'blur(10px)',
              background: alpha(theme.palette.background.paper, 0.6),
            }}>
              {loading ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 2 }}>
                  <CircularProgress size={40} sx={{ mb: 2 }} />
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Loading attribute categories...
                  </Typography>
                </Box>
              ) : (
                <>
                  <Typography variant="h6" sx={{ mb: 2, color: 'text.secondary' }}>
                    No attribute categories defined yet
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary', maxWidth: '600px', textAlign: 'center' }}>
                    Attribute categories let you organize your data with tags like "Wholesaler", "Contract ID", or "Syndicate". 
                    Click "Add Category" to get started.
                  </Typography>
                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={() => setNewCategoryDialog(true)}
                    sx={{
                      borderRadius: '6px',
                      borderColor: alpha(theme.palette.primary.main, 0.5),
                      '&:hover': {
                        borderColor: theme.palette.primary.main,
                        backgroundColor: alpha(theme.palette.primary.main, 0.05),
                      }
                    }}
                  >
                    Add Your First Category
                  </Button>
                </>
              )}
            </StyledPaper>
          </Grid>
        )}
      </Grid>

      <DeleteResourceDialog
        open={Boolean(categoryDeletion.target)}
        title={`Delete ${formatText(categoryDeletion.target?.name || categoryDeletion.target?.id || 'Category')}`}
        preview={categoryDeletion.preview}
        scopeChanged={categoryDeletion.scopeChanged}
        executionError={categoryDeletion.executionError}
        onConfirm={categoryDeletion.confirm}
        onClose={categoryDeletion.close}
        busy={categoryDeletion.busy}
      />

      <Dialog 
        open={newCategoryDialog} 
        onClose={() => {
          setNewCategoryDialog(false);
          setNewCategory({ name: '', description: '', key_tag: false, type: 'Text' });
          setNameError('');
        }}
        PaperProps={{
          sx: {
            borderRadius: 3,
            backgroundColor: theme.palette.background.paper,
            backgroundImage: 'none',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
          }
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ 
          borderBottom: `1px solid ${theme.palette.divider}`, 
          pb: 2,
        }}>
          <Typography variant="h6" component="div" fontWeight={600}>
            Add New Attribute Category
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <TextField
            autoFocus
            margin="dense"
            label="Category Name"
            fullWidth
            variant="outlined"
            value={newCategory.name}
            onChange={(e) => {
              setNewCategory({ ...newCategory, name: e.target.value });
              setNameError('');
            }}
            error={!!nameError}
            helperText={nameError}
            sx={{ mb: 3 }}
            InputProps={{
              className: 'glow-focus',
            }}
          />
          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel id="new-category-type-label">Type</InputLabel>
            <Select
              labelId="new-category-type-label"
              value={newCategory.type}
              label="Type"
              onChange={(e) => setNewCategory({
                ...newCategory,
                type: e.target.value,
                // Only a text category can be a key: a number, date or Yes/No value
                // appears in a file for any reason, so it is never evidence (H14).
                key_tag: e.target.value === 'Text' && newCategory.key_tag,
              })}
            >
              {CATEGORY_TYPE_OPTIONS.map((type) => (
                <MenuItem key={type} value={type}>{type}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            margin="dense"
            label="Description (Optional)"
            fullWidth
            variant="outlined"
            multiline
            rows={3}
            value={newCategory.description}
            onChange={(e) => setNewCategory({ ...newCategory, description: e.target.value })}
            InputProps={{
              className: 'glow-focus',
            }}
            sx={{ mb: 2 }}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={newCategory.key_tag}
                onChange={(e) => setNewCategory({ ...newCategory, key_tag: e.target.checked })}
                disabled={newCategory.type !== 'Text'}
                color="primary"
              />
            }
            label="Key Attribute (determines if this attribute is used to allocate files to a data profile)"
            sx={{ mt: 1 }}
          />
          {newCategory.type !== 'Text' && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 4 }}>
              Only text categories can be key attributes: a number, date or Yes/No value appears in
              files for any reason.
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
          <Button 
            onClick={() => {
              setNewCategoryDialog(false);
              setNewCategory({ name: '', description: '', key_tag: false, type: 'Text' });
              setNameError('');
            }}
            sx={{ 
              color: theme.palette.text.secondary,
              '&:hover': {
                backgroundColor: alpha(theme.palette.divider, 0.1),
              }
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleAddCategory} 
            variant="contained"
            color="primary"
            sx={{
              borderRadius: '6px',
              textTransform: 'none',
              fontWeight: 500,
              '&:hover': {
                backgroundColor: theme.palette.primary.dark,
                boxShadow: '0 4px 10px rgba(0, 0, 0, 0.2)',
              }
            }}
          >
            Add Category
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

ManageAttributes.defaultProps = {
  onAddCategory: () => {}
};

export default ManageAttributes;
